import React, { useState, useMemo, useEffect } from 'react';
import styled from '../../../libs/styled.es.js';
import debounce from 'lodash.debounce';
import { Button } from '../../atoms/Button/Button.es.js';
import { Input } from '../../atoms/Input/Input.es.js';
import { useTheme } from '../../../contexts/ThemeProvider.es.js';

const StyledStepper = styled.div(({ color }) => `
    display: flex;
    align-items: center;
    border-radius: 0.25rem;
    border: 1px solid ${color};
    align-items: center;
    overflow: hidden;
    background: #fff;
    justify-content: space-between;
  `);
const Value = styled.div(({ color }) => `
    flex: 1;
    color: ${color};
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 0.5rem;
    margin-right: 0.5rem;
  `);
const StyledButton = styled(Button)(({ color }) => `
    line-height: 1;
    color: ${color};
    font-size: 1rem;
    border: none;
    padding: 0;
    align-self: stretch;
    padding: 0.5rem;
    border-radius: 0;
    --button-icon-color: ${color};
`);
const Sign = styled.span`
  display: inline-block;
  width: 1rem;
  height: 1rem;
`;
const StyledInput = styled(Input)(({ color }) => `
  padding: 0;
  color: ${color};
  width: 2rem;
  border: none;
  text-align: center;
`);
const Error = styled.div`
  color: #f60202;
  font-size: 0.75rem;
  margin-top: 2px;
`;
const handleFocus = (event) => event.target.select();
const DELAY = 250;
const NumberStepperAllowBlank = ({
  value,
  minimumValue = 0,
  maximumValue,
  stepValue = 1,
  setValue,
  className,
  showDeleteIcon = false,
  variant = "primary",
  showError = false,
  onKeyPress,
  onRemove,
  disabled = false
}) => {
  const theme = useTheme();
  const color = variant === "primary" ? theme.brand.primary : theme.colors.dark;
  const borderColor = variant === "primary" ? theme.brand.primary : theme.colors.gray300;
  const [inputValue, setInputValue] = useState(String(value));
  const [error, setError] = useState("");
  const handleSetValue = (value2) => {
    if (value2 >= minimumValue) {
      setValue(value2);
    }
  };
  const debouncedSetValue = useMemo(() => debounce(handleSetValue, DELAY), [setValue]);
  useEffect(() => {
    setInputValue(String(value));
  }, [value]);
  const handleChange = (e) => {
    if (e.target.value === "" && minimumValue === 0) {
      setInputValue("0");
      debouncedSetValue(null);
    } else if (!Number(e.target.value) && minimumValue !== 0) {
      setInputValue(String(minimumValue));
      debouncedSetValue(null);
    } else {
      let value2 = parseInt(e.target.value);
      if (value2 >= minimumValue) {
        if (value2 > maximumValue) {
          value2 = maximumValue;
          setError(`B\u1EA1n ch\u1EC9 c\xF3 th\u1EC3 tr\u1EA3 t\u1ED1i \u0111a ${maximumValue} s\u1EA3n ph\u1EA9m`);
        } else {
          setError("");
        }
        setInputValue(String(value2));
        debouncedSetValue(value2);
      }
    }
  };
  const handleDecrease = () => {
    if (inputValue === "" && minimumValue !== 0) {
      setInputValue(String(minimumValue));
      debouncedSetValue(minimumValue);
    } else {
      const currentVal = Number(inputValue);
      const newValue = currentVal - stepValue;
      if (currentVal === minimumValue) {
        onRemove();
      } else if (newValue >= minimumValue) {
        setError("");
        newValue !== 0 ? setInputValue(String(newValue)) : setInputValue("0");
        debouncedSetValue(newValue);
      }
    }
  };
  const handleIncrease = () => {
    if (inputValue === "" && minimumValue !== 0) {
      setInputValue(String(minimumValue));
      debouncedSetValue(minimumValue);
    } else {
      const currentVal = Number(inputValue);
      if (maximumValue >= currentVal + stepValue) {
        setError("");
        setInputValue(String(currentVal + stepValue));
        debouncedSetValue(currentVal + stepValue);
      } else {
        setError(`B\u1EA1n ch\u1EC9 c\xF3 th\u1EC3 tr\u1EA3 t\u1ED1i \u0111a ${maximumValue} s\u1EA3n ph\u1EA9m`);
      }
    }
  };
  const disableIncrease = !!error || Number(inputValue) === maximumValue;
  const disableDecrease = Number(inputValue) === minimumValue;
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(StyledStepper, {
    className,
    color: borderColor
  }, /* @__PURE__ */ React.createElement(StyledButton, {
    color: disableDecrease ? borderColor : color,
    variant,
    outline: true,
    onClick: handleDecrease,
    disabled: !showDeleteIcon && Number(inputValue) === minimumValue,
    icon: inputValue === String(minimumValue) && showDeleteIcon ? {
      name: "trash",
      size: 16
    } : null
  }, (!showDeleteIcon || Number(inputValue) > minimumValue || !inputValue) && /* @__PURE__ */ React.createElement(Sign, null, "-")), setValue ? /* @__PURE__ */ React.createElement(StyledInput, {
    type: "number",
    inputMode: "numeric",
    onChange: handleChange,
    value: inputValue,
    onFocus: handleFocus,
    onBlur: () => {
      if (!inputValue || Number(inputValue) < minimumValue) {
        setInputValue(String(minimumValue));
        debouncedSetValue(minimumValue);
      }
    },
    onKeyPress,
    disabled
  }) : /* @__PURE__ */ React.createElement(Value, null, value), /* @__PURE__ */ React.createElement(StyledButton, {
    color: disableIncrease ? borderColor : color,
    variant,
    outline: true,
    onClick: handleIncrease,
    disabled: disableIncrease
  }, /* @__PURE__ */ React.createElement(Sign, null, "+"))), error !== "" && showError && /* @__PURE__ */ React.createElement(Error, null, error));
};

export { NumberStepperAllowBlank };
